import React, { useState, useEffect } from 'react';
import {
   Dialog,
   DialogContent,
   DialogDescription,
   DialogFooter,
   DialogHeader,
   DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";

const PTPRPresetModal = ({ isOpen, onClose, onSave, initialPreset }) => {
   const defaultSelectedDocuments = {
      letterRequest: true,           // Default required docs are checked
      titleOrTaxDeclaration: true,   // Default required docs are checked
      darCertification: false,       // Conditional on isCLOA
      specialPowerOfAttorney: false  // Conditional on isRepresentative
   };

   const [preset, setPreset] = useState({
      ownerName: '',
      address: '',
      contactNumber: '',
      isCLOA: false,
      isRepresentative: false,
      selectedDocuments: defaultSelectedDocuments
   });

   useEffect(() => {
      if (initialPreset) {
         setPreset({
            ...initialPreset,
            selectedDocuments: {
               ...defaultSelectedDocuments,
               ...(initialPreset.selectedDocuments || {})
            }
         });
      }
   }, [initialPreset]);

   const handleCheckboxChange = (name, checked) => {
      setPreset(prev => ({
         ...prev,
         [name]: checked,
         selectedDocuments: {
            ...prev.selectedDocuments,
            darCertification: name === 'isCLOA' ? checked : prev.selectedDocuments.darCertification,
            specialPowerOfAttorney: name === 'isRepresentative' ? checked : prev.selectedDocuments.specialPowerOfAttorney,
         }
      }));
   };

   const renderFileRequirements = () => {
      const fileTypes = [
         { type: 'letterRequest', label: 'Letter Request', condition: true, path: 'letter-request.jpg' },
         { type: 'titleOrTaxDeclaration', label: 'Title or Tax Declaration', condition: true, path: 'tax-declaration.jpg' },
         { type: 'darCertification', label: 'DAR Certification', condition: preset.isCLOA, path: 'DAR-certification.png' },
         { type: 'specialPowerOfAttorney', label: 'Special Power of Attorney', condition: preset.isRepresentative, path: 'Special-Power-of-Attorney.png' }
      ];

      return fileTypes.filter(fileType => fileType.condition).map(({ type, label, path }) => (
         <div key={type} className="flex items-center space-x-2 mb-4">
            <Checkbox
               id={type}
               checked={preset.selectedDocuments[type]}
               onCheckedChange={(checked) => {
                  setPreset(prev => ({
                     ...prev,
                     selectedDocuments: {
                        ...prev.selectedDocuments,
                        [type]: checked
                     }
                  }));
               }}
            />
            <div>
               <Label htmlFor={type} className="text-sm text-muted-foreground">{label}</Label>
               <div className="mt-1 text-xs text-gray-500">
                  {type ? 'Required' : 'Optional'}
               </div>
            </div>
         </div>
      ));
   };

   const handleSave = () => {
      const { files, ...presetToSave } = preset;
      console.log('Saving PTPR preset:', presetToSave);
      localStorage.setItem('ptprPreset', JSON.stringify(presetToSave));
      onSave(presetToSave);
      onClose();
      toast.success('PTPR preset saved successfully');
   };

   return (
      <Dialog open={isOpen} onOpenChange={onClose}>
         <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
               <DialogTitle>PTPR Application Preset</DialogTitle>
               <DialogDescription>
                  Configure default values for PTPR test applications
               </DialogDescription>
            </DialogHeader>

            <ScrollArea className="max-h-[500px] pr-4">
               <div className="space-y-4 pb-4">
                  <div className="space-y-2">
                     <Label className="text-base">Additional Information</Label>
                     <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                           <Checkbox
                              id="isCLOA"
                              checked={preset.isCLOA}
                              onCheckedChange={(checked) => handleCheckboxChange('isCLOA', checked)}
                           />
                           <Label htmlFor="isCLOA">Is Title Certificate of Land Ownership Award (CLOA)</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                           <Checkbox
                              id="isRepresentative"
                              checked={preset.isRepresentative}
                              onCheckedChange={(checked) => handleCheckboxChange('isRepresentative', checked)}
                           />
                           <Label htmlFor="isRepresentative">Is Representative</Label>
                        </div>
                     </div>
                  </div>

                  <div className="border-t border-gray-200 pt-4 mt-4">
                     <h3 className="text-lg font-semibold mb-4">Required Documents</h3>
                     {renderFileRequirements()}
                  </div>
               </div>
            </ScrollArea>

            <DialogFooter>
               <Button variant="outline" onClick={onClose}>
                  Cancel
               </Button>
               <Button onClick={handleSave}>Save Preset</Button>
            </DialogFooter>
         </DialogContent>
      </Dialog>
   );
};

export default PTPRPresetModal;